import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { 
  Star, ShoppingCart, Minus, Plus, Heart, Share2, 
  Loader2, ChevronLeft, ChevronRight, Check, Truck, Shield, Package, BadgeCheck, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import '@/styles/shop.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ProductPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { user, refreshCart } = useOutletContext();
  
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [addingToCart, setAddingToCart] = useState(false);
  const [isWishlisted, setIsWishlisted] = useState(false);

  useEffect(() => {
    fetchProduct();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/shop/products/${productId}`);
      setProduct(response.data);
    } catch (error) {
      toast.error('Product not found');
      navigate('/shop');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async () => {
    if (!user) {
      toast.error('Please login to add items to cart');
      navigate('/auth');
      return;
    }

    setAddingToCart(true);
    try {
      const token = localStorage.getItem('dharmbook_token');
      await axios.post(`${API}/shop/cart`, 
        { product_id: productId, quantity },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Added to cart!');
      refreshCart();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add to cart');
    } finally {
      setAddingToCart(false);
    }
  };

  const handleBuyNow = async () => {
    if (!user) {
      toast.error('Please login to continue');
      navigate('/auth');
      return;
    }
    
    await handleAddToCart();
    navigate('/shop/cart');
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: product.name,
          text: `Check out ${product.name} on DharmShop`,
          url
        });
      } catch (e) {}
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard');
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <Loader2 className="w-10 h-10 animate-spin text-amber-500" />
        <p className="text-amber-700">Loading product...</p>
      </div>
    );
  }

  if (!product) {
    return null;
  }

  const hasDiscount = product.original_price && product.original_price > product.price;
  const discountPercent = hasDiscount 
    ? Math.round((1 - product.price / product.original_price) * 100) 
    : 0;
  const savings = hasDiscount ? product.original_price - product.price : 0;

  const images = product.images?.length > 0 
    ? product.images 
    : ['https://images.unsplash.com/photo-1633617127680-18229ebb1b94?w=600'];

  return (
    <div className="shop-page">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-100 py-3 px-4">
        <div className="shop-container flex items-center gap-2 text-sm">
          <Link to="/shop" className="text-gray-500 hover:text-amber-600 transition-colors">Home</Link>
          <ChevronRight className="w-4 h-4 text-gray-300" />
          <Link to={`/shop/category/${product.category_id}`} className="text-gray-500 hover:text-amber-600 transition-colors">
            {product.category_name}
          </Link>
          <ChevronRight className="w-4 h-4 text-gray-300" />
          <span className="text-gray-800 font-medium truncate max-w-[200px]">{product.name}</span>
        </div>
      </div>

      <div className="shop-container shop-product-detail">
        <div className="shop-product-detail-grid">
          {/* Image Gallery - Left Side */}
          <div className="shop-gallery">
            {/* Main Image */}
            <div className="shop-gallery-main relative">
              <img
                src={images[selectedImage]}
                alt={product.name}
              />
              
              {/* Image Navigation */}
              {images.length > 1 && (
                <>
                  <button
                    onClick={() => setSelectedImage((selectedImage - 1 + images.length) % images.length)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/95 rounded-full flex items-center justify-center shadow-lg hover:bg-white border border-gray-100"
                    data-testid="prev-image-btn"
                  >
                    <ChevronLeft className="w-5 h-5 text-gray-700" />
                  </button>
                  <button
                    onClick={() => setSelectedImage((selectedImage + 1) % images.length)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/95 rounded-full flex items-center justify-center shadow-lg hover:bg-white border border-gray-100"
                    data-testid="next-image-btn"
                  >
                    <ChevronRight className="w-5 h-5 text-gray-700" />
                  </button>
                </>
              )}

              {/* Badges */}
              <div className="absolute top-4 left-4 flex flex-col gap-2">
                {product.is_featured && (
                  <span className="px-3 py-1.5 bg-amber-500 text-white text-xs font-bold rounded-lg flex items-center gap-1 shadow-md">
                    <Sparkles className="w-3 h-3" /> Featured
                  </span>
                )}
                {hasDiscount && (
                  <span className="px-3 py-1.5 bg-green-600 text-white text-xs font-bold rounded-lg shadow-md">
                    {discountPercent}% OFF
                  </span>
                )}
              </div>
            </div>

            {/* Thumbnails */}
            {images.length > 1 && (
              <div className="shop-gallery-thumbnails">
                {images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`shop-gallery-thumb ${selectedImage === idx ? 'active' : ''}`}
                    data-testid={`thumb-${idx}`}
                  >
                    <img src={img} alt={`${product.name} ${idx + 1}`} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info - Right Side */}
          <div className="shop-product-panel">
            {/* Category */}
            <Link 
              to={`/shop/category/${product.category_id}`}
              className="shop-product-panel-category hover:underline"
            >
              {product.category_name}
            </Link>

            {/* Title */}
            <h1 className="shop-product-panel-title">{product.name}</h1>
            {product.name_hindi && (
              <p className="text-amber-600 mb-3 text-lg">{product.name_hindi}</p>
            )}

            {/* Rating */}
            <div className="shop-product-panel-rating">
              {product.rating > 0 && (
                <>
                  <div className="flex items-center gap-1 bg-green-600 text-white px-2.5 py-1 rounded-lg">
                    <Star className="w-4 h-4 fill-current" />
                    <span className="font-bold">{product.rating}</span>
                  </div>
                  <span className="text-gray-500">({product.reviews_count} reviews)</span>
                </>
              )}
              {!product.rating && (
                <span className="text-gray-500 text-sm">No reviews yet</span>
              )}
            </div>

            {/* Price Section */}
            <div className="shop-product-panel-price-section">
              <div className="flex items-baseline">
                <span className="shop-product-panel-price">₹{product.price}</span>
                {hasDiscount && (
                  <span className="shop-product-panel-original-price">₹{product.original_price}</span>
                )}
              </div>
              {hasDiscount && (
                <div className="shop-product-panel-savings">
                  <Check className="w-4 h-4" />
                  You save ₹{savings} ({discountPercent}% OFF)
                </div>
              )}
              <p className="text-xs text-gray-500 mt-2">Inclusive of all taxes</p>
            </div>

            {/* Description */}
            <div className="mb-6">
              <h3 className="font-semibold text-gray-800 mb-2">Description</h3>
              <p className="text-gray-600 leading-relaxed text-sm">{product.description}</p>
            </div>

            {/* Specifications */}
            {product.specifications && Object.keys(product.specifications).length > 0 && (
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <h3 className="font-semibold text-gray-800 mb-3">Specifications</h3>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(product.specifications).map(([key, value]) => (
                    <div key={key} className="flex justify-between gap-2 py-1">
                      <span className="text-gray-500 text-sm">{key}</span>
                      <span className="text-gray-800 text-sm font-medium text-right">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Stock Status */}
            <div className="flex items-center gap-2 mb-6">
              {product.stock > 0 ? (
                <>
                  <div className="flex items-center gap-1.5 text-green-600">
                    <Check className="w-5 h-5" />
                    <span className="font-semibold">In Stock</span>
                  </div>
                  {product.stock < 10 && (
                    <span className="text-orange-500 text-sm font-medium bg-orange-50 px-2 py-0.5 rounded">
                      Only {product.stock} left!
                    </span>
                  )}
                </>
              ) : (
                <span className="text-red-500 font-semibold">Out of Stock</span>
              )}
            </div>

            {/* Quantity Selector */}
            {product.stock > 0 && (
              <div className="shop-quantity-selector">
                <span className="shop-quantity-label">Quantity:</span>
                <div className="shop-quantity-controls">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="shop-quantity-btn"
                    data-testid="qty-minus"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <input
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(Math.min(Math.max(1, parseInt(e.target.value) || 1), product.stock))}
                    className="shop-quantity-value"
                    min={1}
                    max={product.stock}
                    data-testid="qty-input"
                  />
                  <button
                    onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                    className="shop-quantity-btn"
                    data-testid="qty-plus"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="shop-action-buttons">
              <button
                onClick={handleAddToCart}
                disabled={addingToCart || product.stock === 0}
                className="shop-btn-add-to-cart"
                data-testid="add-to-cart-btn"
              >
                {addingToCart ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <ShoppingCart className="w-5 h-5" />
                )}
                Add to Cart
              </button>
              <button
                onClick={handleBuyNow}
                disabled={product.stock === 0}
                className="shop-btn-buy-now"
                data-testid="buy-now-btn"
              >
                Buy Now
              </button>
            </div>

            {/* Secondary Actions */}
            <div className="flex items-center gap-4 mb-8">
              <button
                onClick={() => setIsWishlisted(!isWishlisted)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg transition-all border ${
                  isWishlisted 
                    ? 'bg-red-50 text-red-500 border-red-200' 
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border-gray-200'
                }`}
                data-testid="wishlist-btn"
              >
                <Heart className={`w-5 h-5 ${isWishlisted ? 'fill-current' : ''}`} />
                <span className="text-sm font-medium">{isWishlisted ? 'Wishlisted' : 'Wishlist'}</span>
              </button>
              <button
                onClick={handleShare}
                className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 text-gray-600 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
                data-testid="share-btn"
              >
                <Share2 className="w-5 h-5" />
                <span className="text-sm font-medium">Share</span>
              </button>
            </div>

            {/* Trust Badges */}
            <div className="shop-trust-badges">
              <div className="shop-trust-badge">
                <div className="shop-trust-badge-icon" style={{ background: '#FEF3C7' }}>
                  <Truck className="w-5 h-5 text-amber-600" />
                </div>
                <p className="shop-trust-badge-title">Free Delivery</p>
                <p className="shop-trust-badge-desc">Over ₹500</p>
              </div>
              <div className="shop-trust-badge">
                <div className="shop-trust-badge-icon" style={{ background: '#ECFDF5' }}>
                  <BadgeCheck className="w-5 h-5 text-green-600" />
                </div>
                <p className="shop-trust-badge-title">Authentic</p>
                <p className="shop-trust-badge-desc">100% Genuine</p>
              </div>
              <div className="shop-trust-badge">
                <div className="shop-trust-badge-icon" style={{ background: '#EFF6FF' }}>
                  <Shield className="w-5 h-5 text-blue-600" />
                </div>
                <p className="shop-trust-badge-title">Secure</p>
                <p className="shop-trust-badge-desc">Safe Payment</p>
              </div>
            </div>
          </div>
        </div>

        {/* Product Details Tabs - Below */}
        <div className="mt-8 bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="border-b border-gray-200">
            <nav className="flex">
              <button className="px-6 py-4 text-sm font-semibold text-amber-600 border-b-2 border-amber-500">
                Description
              </button>
              <button className="px-6 py-4 text-sm font-medium text-gray-500 hover:text-gray-700">
                Reviews ({product.reviews_count || 0})
              </button>
              <button className="px-6 py-4 text-sm font-medium text-gray-500 hover:text-gray-700">
                Shipping Info
              </button>
            </nav>
          </div>
          <div className="p-6">
            <div className="prose prose-sm max-w-none">
              <p className="text-gray-600 leading-relaxed">{product.description}</p>
              
              {product.specifications && Object.keys(product.specifications).length > 0 && (
                <div className="mt-6">
                  <h4 className="font-semibold text-gray-800 mb-3">Product Specifications</h4>
                  <table className="w-full text-sm">
                    <tbody>
                      {Object.entries(product.specifications).map(([key, value]) => (
                        <tr key={key} className="border-b border-gray-100">
                          <td className="py-2 text-gray-500 w-1/3">{key}</td>
                          <td className="py-2 text-gray-800 font-medium">{value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
